import React, { FormEventHandler, ReactNode, useMemo, useRef } from "react";

import { cn } from "@shared/ui/utils/tailwind";
import ServerErrorPopUp from "@components/ui/feedback/ServerErrorPopUp";
import ThemeToggle from "@components/ui/buttons/ThemeToggle";
import HousienLogo from "@components/business/auth/HousienLogo";
import ClientHeader from "@components/business/auth/ClientHeader";
import { getClientSubtitle } from "@shared/utils/client";
import { useOAuth2 } from "@shared/contexts";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { useIsMobileButtons } from "@shared/hooks/useIsMobileButtons";
import { useTranslation } from "react-i18next";
import { useFormRecaptcha } from "./hooks/useFormRecaptcha";
import { useFormAnimation } from "./hooks/useFormAnimation";
import { useFormKeyboardNavigation } from "./hooks/useFormKeyboardNavigation";
import NavigationButtons from "./NavigationButtons";
import { FF_NIGHT_MODE } from "@shared/utils/feature-flag";

/** Props for the main authentication form layout container. */
interface LoginContainerProps {
  onSubmit?: FormEventHandler<HTMLFormElement>;
  children: ReactNode;
  title?: string;
  subtitle?: string;
  back?: Function;
  isLoading?: boolean;
  serverErrorPopUp?: string | null;
  setServerErrorPopUp?: React.Dispatch<React.SetStateAction<string | null>>;
  mobileFooter?: ReactNode;
  currentStep?: number;
  totalSteps?: number;
  hideClientHeader?: boolean;
  centerContent?: boolean;
}

/** Derived layout flags controlling which navigation and progress elements render. */
interface LayoutFlags {
  showMobileNav: boolean;
  showDesktopNav: boolean;
  hasMobileFooter: boolean;
  showProgress: boolean;
}

function useFormSubmitHandler(
  isLoading: boolean | undefined,
  onSubmit: FormEventHandler<HTMLFormElement>,
  handleRecaptchaExecution: () => Promise<void>,
  formRef: React.RefObject<HTMLFormElement | null>
) {
  const preventDefault: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    try {
      await handleRecaptchaExecution();
      await onSubmit(e);
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleMobileSubmit = () => {
    if (!formRef.current) return;
    formRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  };

  return { preventDefault, handleMobileSubmit };
}

function hasBackNavigation(back: Function | undefined): boolean {
  return !!back;
}

function shouldShowMobileNav(isMobileButtons: boolean, hasBack: boolean): boolean {
  return isMobileButtons && hasBack;
}

function shouldShowDesktopNav(isMobileButtons: boolean, hasBack: boolean): boolean {
  return !isMobileButtons && hasBack;
}

function shouldShowMobileFooter(isMobile: boolean, hasMobileContent: boolean): boolean {
  return isMobile && hasMobileContent;
}

function shouldShowProgress(currentStep: number | undefined, totalSteps: number | undefined): boolean {
  return currentStep != null && totalSteps != null && totalSteps > 1;
}

function resolveLayoutFlags(
  isMobile: boolean,
  isMobileButtons: boolean,
  back: Function | undefined,
  mobileFooter: ReactNode | undefined,
  currentStep: number | undefined,
  totalSteps: number | undefined
): LayoutFlags {
  const hasBack = hasBackNavigation(back);
  const showMobileNav = shouldShowMobileNav(isMobileButtons, hasBack);
  const showDesktopNav = shouldShowDesktopNav(isMobileButtons, hasBack);
  const hasMobileFooter = shouldShowMobileFooter(isMobile, !!mobileFooter || showMobileNav);
  const showProgress = shouldShowProgress(currentStep, totalSteps);
  return { showMobileNav, showDesktopNav, hasMobileFooter, showProgress };
}

const StepProgress: React.FC<{ current: number; total: number }> = ({ current, total }) => {
  if (total <= 1) return null;
  const pct = Math.round((current / total) * 100);
  return (
    <div className="w-full h-1 bg-(--border-subtle) mb-4" role="progressbar" aria-valuenow={current} aria-valuemin={1} aria-valuemax={total}>
      <div className="h-full bg-(--accent-primary) transition-[width] duration-300 ease-out" style={{ width: `${pct}%` }} />
    </div>
  );
};

const ErrorPopUpSection: React.FC<{ errorData?: string | null; setErrorData?: React.Dispatch<React.SetStateAction<string | null>> }> = ({ errorData, setErrorData }) => {
  if (!errorData || !setErrorData) return null;
  return <ServerErrorPopUp errorData={errorData} setErrorData={setErrorData} />;
};

interface MobileFooterProps {
  show: boolean;
  mobileFooter?: ReactNode;
  back?: Function;
  isLoading: boolean;
  onMobileSubmit: () => void;
}

const MobileFooterSection: React.FC<MobileFooterProps> = ({ show, mobileFooter, back, isLoading, onMobileSubmit }) => {
  if (!show) return null;
  return (
    <div className="auth-mobile-footer">
      {mobileFooter ?? (
        <NavigationButtons back={back} isLoading={isLoading} isMobileNav={true} onMobileSubmit={onMobileSubmit} />
      )}
    </div>
  );
};

interface LayoutShellProps {
  isLoading: boolean | undefined;
  containerStyle: React.CSSProperties;
  hasAnimated: boolean;
  formContent: ReactNode;
  serverErrorPopUp?: string | null;
  setServerErrorPopUp?: React.Dispatch<React.SetStateAction<string | null>>;
}

const MobileLayout: React.FC<LayoutShellProps & MobileFooterProps> = ({
  isLoading, containerStyle, hasAnimated, formContent, serverErrorPopUp, setServerErrorPopUp,
  show, mobileFooter, back, isLoading: footerLoading, onMobileSubmit,
}) => (
  <div className={cn("auth-mobile-layout", isLoading && "opacity-50")} style={containerStyle}>
    <div className="auth-mobile-header">
      <HousienLogo size="sm" />
    </div>
    <div className="auth-mobile-content">
      <div className={cn("w-full max-w-lg mx-auto", !hasAnimated && "slide-from-right")}>
        {formContent}
        {FF_NIGHT_MODE && (
          <div className="flex justify-start" style={{ marginTop: 'clamp(0.25rem, 1dvh, 0.5rem)' }}>
            <ThemeToggle />
          </div>
        )}
      </div>
    </div>
    <MobileFooterSection show={show} mobileFooter={mobileFooter} back={back} isLoading={footerLoading} onMobileSubmit={onMobileSubmit} />
    <ErrorPopUpSection errorData={serverErrorPopUp} setErrorData={setServerErrorPopUp} />
  </div>
);

const DesktopLayout: React.FC<LayoutShellProps> = ({
  isLoading, containerStyle, hasAnimated, formContent, serverErrorPopUp, setServerErrorPopUp,
}) => (
  <div className={cn("auth-desktop-layout", isLoading && "opacity-50")} style={containerStyle}>
    <div className="h-[72px] px-4 flex items-center shrink-0 w-full">
      <HousienLogo size="lg" />
    </div>
    <div className="flex-1 flex justify-center items-center w-full">
      <div className={cn("w-full max-w-md lg:max-w-md md:max-w-lg sm:max-w-lg", !hasAnimated && "slide-from-right", "auth-card")}>
        <div className="w-full auth-card-padding">
          {formContent}
          {FF_NIGHT_MODE && (
            <div className="mt-4 flex justify-start">
              <ThemeToggle />
            </div>
          )}
        </div>
      </div>
    </div>
    <ErrorPopUpSection errorData={serverErrorPopUp} setErrorData={setServerErrorPopUp} />
  </div>
);

const ProgressSection: React.FC<{ show: boolean; current: number; total: number }> = ({ show, current, total }) => {
  if (!show) return null;
  return <StepProgress current={current} total={total} />;
};

const DesktopNavSection: React.FC<{ show: boolean; back?: Function; isLoading: boolean }> = ({ show, back, isLoading }) => {
  if (!show) return null;
  return <NavigationButtons back={back} isLoading={isLoading} isMobileNav={false} />;
};

function useContainerStyle(isLoading: boolean | undefined) {
  return useMemo(() => ({
    pointerEvents: isLoading ? "none" as const : "auto" as const,
  }), [isLoading]);
}

const NOOP_SUBMIT: FormEventHandler<HTMLFormElement> = () => {};

interface ResolvedProps {
  onSubmit: FormEventHandler<HTMLFormElement>;
  currentStep: number;
  totalSteps: number;
  loadingValue: boolean;
}

function resolveDefaults(props: LoginContainerProps): ResolvedProps {
  return {
    onSubmit: props.onSubmit ?? NOOP_SUBMIT,
    currentStep: props.currentStep ?? 0,
    totalSteps: props.totalSteps ?? 0,
    loadingValue: props.isLoading ?? false,
  };
}

const AuthenticationForm: React.FC<LoginContainerProps> = (props) => {
  const { children, title, subtitle, back, isLoading, serverErrorPopUp, setServerErrorPopUp, mobileFooter, hideClientHeader, centerContent } = props;
  const { onSubmit, currentStep, totalSteps, loadingValue } = resolveDefaults(props);
  const { t } = useTranslation();
  const { client } = useOAuth2();
  const isMobile = useIsMobile();
  const isMobileButtons = useIsMobileButtons();
  const formRef = useRef<HTMLFormElement>(null);

  const { handleRecaptchaExecution } = useFormRecaptcha();
  const hasAnimated = useFormAnimation();
  useFormKeyboardNavigation(back);

  const { preventDefault, handleMobileSubmit } = useFormSubmitHandler(isLoading, onSubmit, handleRecaptchaExecution, formRef);
  const containerStyle = useContainerStyle(isLoading);
  const flags = resolveLayoutFlags(isMobile, isMobileButtons, back, mobileFooter, currentStep, totalSteps);

  const formContent = (
    <form ref={formRef} noValidate={true} onSubmit={preventDefault} className={cn(isMobile ? "flex flex-col" : "space-y-5")} style={isMobile ? { gap: 'clamp(0.25rem, 1.2dvh, 0.75rem)' } : undefined}>
      <ProgressSection show={flags.showProgress} current={currentStep} total={totalSteps} />
      {!hideClientHeader && <ClientHeader clientName={getClientSubtitle(client, t)} />}
      {title && (
        <div className={cn("space-y-1", centerContent && "text-center")}>
          <h2 className="text-xl font-semibold text-(--text-primary) leading-tight">{title}</h2>
          {subtitle && <p className="text-sm text-(--text-secondary) leading-normal">{subtitle}</p>}
        </div>
      )}
      {children}
      <DesktopNavSection show={flags.showDesktopNav} back={back} isLoading={loadingValue} />
    </form>
  );

  const sharedProps = { isLoading: loadingValue, containerStyle, hasAnimated, formContent, serverErrorPopUp, setServerErrorPopUp };

  if (isMobile) {
    return (
      <MobileLayout
        {...sharedProps}
        show={flags.hasMobileFooter}
        mobileFooter={mobileFooter}
        back={back}
        onMobileSubmit={handleMobileSubmit}
      />
    );
  }

  return <DesktopLayout {...sharedProps} />;
};

export { AuthenticationForm };
export default AuthenticationForm;
